/**
 * FeeCalculatorCard — P22 (team feedback fb 4/13; Claude's proposal, Pedro "ok"
 * 2026-10-10), on the Blank legal templates page.
 *
 * Pick a schedule (the firm Standard by default, or any Custom one), an amount
 * (default €1,000,000) and a billing frequency (Pedro 2026-10-10: only that frequency is
 * shown, on screen and in the Annex I example): each tranche's share, the annual fee, and
 * the fee per billing period with VAT and total. Every figure comes from the backend
 * (`GET /fee-schedules/{id}/example`), which uses the fee engine's own functions, so
 * the example cannot disagree with what would actually be billed.
 *
 * Pedro 2026-10-10 (smoke): ONE download path. The calculator no longer has its own
 * Annex I buttons; ticking an Annex I PDF in the templates grid and pressing Download asks
 * whether to include a fee simulation and, on Yes, opens this same calculator in a dialog.
 * `FeeCalculator` is the shared body. It is controlled, so the page card and the dialog
 * show the same figures; `FeeCalculatorCard` is the page card around it.
 */
import {
  Alert, Card, Group, NumberInput, Select, Stack, Table, Text, Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCalculator } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { ApiError } from '../api/client';
import {
  bpsToPct, feeSchedulesApi, fmtEurFromCents, type FeeExampleOut, type FeeFrequency,
} from '../api/feeSchedules';

export const FREQ_LABEL: Record<FeeFrequency, string> = {
  monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semiannual',
};

/** The calculator's inputs. `scheduleId` null = not chosen yet (defaults to the first
 *  offered schedule). `amount` null = not typed yet (a Prospect with no AuM). `vatRateBps`
 *  omitted = the server default (23%); an agreement's simulation uses its accounts' VAT. */
export interface FeeCalcValue {
  scheduleId: string | null;
  amount: number | null;
  frequency: FeeFrequency;
  vatRateBps?: number | null;
}

export const DEFAULT_FEE_CALC: FeeCalcValue = { scheduleId: null, amount: 1_000_000, frequency: 'monthly' };

function useScheduleOptions(onlyIds?: string[]) {
  const standardQ = useQuery({ queryKey: ['fee-schedules', 'standard-history'],
    queryFn: feeSchedulesApi.standardHistory });
  const customQ = useQuery({ queryKey: ['fee-schedules', 'custom'],
    queryFn: feeSchedulesApi.listCustom });
  const options = useMemo(() => {
    // An agreement's simulation offers only the schedules its accounts use (Pedro 2026-10-10).
    const offered = (id: string) => !onlyIds || onlyIds.includes(id);
    const std = (standardQ.data ?? []).filter((s) => !s.deleted_at && offered(s.id)
      && (s.status === 'active' || !!onlyIds))
      .map((s) => ({ value: s.id, label: `Standard${s.name ? ` · ${s.name}` : ''} (v${s.version})` }));
    const custom = (customQ.data ?? []).filter((s) => !s.deleted_at && offered(s.id))
      .map((s) => ({ value: s.id, label: s.name ?? 'Custom schedule' }));
    return [
      ...(std.length ? [{ group: 'Standard', items: std }] : []),
      ...(custom.length ? [{ group: 'Custom', items: custom }] : []),
    ];
  }, [standardQ.data, customQ.data, onlyIds]);
  return { options, loading: standardQ.isLoading || customQ.isLoading };
}

/** The worked example for the current inputs, recomputed once typing pauses (the
 *  EntityPicker idiom). The dialog's Continue button reads the same query (same key,
 *  one request) to know the example is ready. */
export function useFeeExample(value: FeeCalcValue) {
  const [debouncedAmount] = useDebouncedValue(value.amount, 300);
  const vat = value.vatRateBps ?? null;
  return useQuery<FeeExampleOut, ApiError>({
    queryKey: ['fee-schedules', 'example', value.scheduleId, debouncedAmount, value.frequency, vat],
    queryFn: () => feeSchedulesApi.example(value.scheduleId!, debouncedAmount!, value.frequency, vat),
    enabled: !!value.scheduleId && debouncedAmount !== null && debouncedAmount >= 0,
    placeholderData: keepPreviousData,
  });
}

/** The calculator body: the three inputs and the worked example. `scheduleIds` limits the
 *  schedule picker (an agreement's own schedules). */
export function FeeCalculator({ value, onChange, scheduleIds }: {
  value: FeeCalcValue;
  onChange: (v: FeeCalcValue) => void;
  scheduleIds?: string[];
}) {
  const { options, loading } = useScheduleOptions(scheduleIds);
  // Default to the active Standard once it loads.
  useEffect(() => {
    if (!value.scheduleId && options[0]?.items[0]) onChange({ ...value, scheduleId: options[0].items[0].value });
  }, [options, value, onChange]);

  const exampleQ = useFeeExample(value);
  const ex = exampleQ.data;

  return (
    <Stack>
      <Group align="flex-end" grow>
        <Select label="Fee schedule" data={options} value={value.scheduleId}
          onChange={(v) => onChange({ ...value, scheduleId: v })} allowDeselect={false} searchable
          placeholder={loading ? 'Loading…' : 'No schedules'}
          comboboxProps={{ withinPortal: true }} />
        <NumberInput label="Amount (€)" value={value.amount ?? ''} min={0} step={100_000}
          thousandSeparator="," decimalScale={2} placeholder="Type the amount"
          onChange={(v) => onChange({ ...value, amount: typeof v === 'number' ? v : null })} />
        <Select label="Billing frequency" allowDeselect={false} value={value.frequency}
          data={(Object.keys(FREQ_LABEL) as FeeFrequency[])
            .map((f) => ({ value: f, label: FREQ_LABEL[f] }))}
          onChange={(v) => v && onChange({ ...value, frequency: v as FeeFrequency })}
          comboboxProps={{ withinPortal: true }} />
      </Group>

      {exampleQ.error && (
        <Alert color="red" title="Could not compute the example">{exampleQ.error.detail}</Alert>
      )}

      {ex && value.amount !== null && <FeeExampleView ex={ex} />}
    </Stack>
  );
}

/** The worked example's tables: each tranche's share, the annual fee and effective rate,
 *  and the fee per billing period (the periods the example was asked for). Shared by the
 *  calculator and the Agreement's per-schedule simulation (Pedro 2026-10-11). */
export function FeeExampleView({ ex }: { ex: FeeExampleOut }) {
  return (
    <Stack gap="sm">
      {ex.lines.length > 0 && (
        <Table withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tranche</Table.Th>
              <Table.Th ta="right">Annual rate</Table.Th>
              <Table.Th ta="right">Amount in tranche (€)</Table.Th>
              <Table.Th ta="right">Annual fee (€)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ex.lines.map((l) => (
              <Table.Tr key={l.min_cents}>
                <Table.Td>
                  {l.max_cents === null
                    ? `Above €${fmtEurFromCents(l.min_cents)}`
                    : `€${fmtEurFromCents(l.min_cents)} – €${fmtEurFromCents(l.max_cents)}`}
                </Table.Td>
                <Table.Td ta="right">{bpsToPct(l.rate_bps).toFixed(2)}%</Table.Td>
                <Table.Td ta="right">{fmtEurFromCents(l.portion_cents)}</Table.Td>
                <Table.Td ta="right">{fmtEurFromCents(l.fee_cents)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Group gap="xl">
        <Text size="sm">Annual fee: <b>€{fmtEurFromCents(ex.annual_fee_cents)}</b> (ex-VAT)</Text>
        {ex.effective_rate_pct !== null && (
          <Text size="sm">Effective rate: <b>{ex.effective_rate_pct.toFixed(3)}%</b></Text>
        )}
        {ex.kind === 'fixed' && (
          <Text size="sm" c="dimmed">A fixed fee: the amount does not change it.</Text>
        )}
      </Group>
      <Table withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Billing</Table.Th>
            <Table.Th ta="right">Fee per period, ex-VAT (€)</Table.Th>
            <Table.Th ta="right">VAT {bpsToPct(ex.vat_rate_bps)}% (€)</Table.Th>
            <Table.Th ta="right">Total per period (€)</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {ex.periods.map((p) => (
            <Table.Tr key={p.frequency}>
              <Table.Td>{FREQ_LABEL[p.frequency]} ({p.periods_per_year} a year)</Table.Td>
              <Table.Td ta="right">{fmtEurFromCents(p.net_fee_cents)}</Table.Td>
              <Table.Td ta="right">{fmtEurFromCents(p.vat_cents)}</Table.Td>
              <Table.Td ta="right">{fmtEurFromCents(p.total_fee_cents)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {ex.lines.length > 1 && (
        <Text size="xs" c="dimmed">
          Each tranche's fee is rounded for display; the annual fee is computed
          exactly and rounded once, so it can differ from their sum by a cent.
        </Text>
      )}
    </Stack>
  );
}

export function FeeCalculatorCard({ value, onChange }: {
  value: FeeCalcValue;
  onChange: (v: FeeCalcValue) => void;
}) {
  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group gap="xs">
          <IconCalculator size={18} />
          <Title order={4}>Fee calculator</Title>
        </Group>
        <Text size="sm" c="dimmed">
          A worked example of a fee schedule at a given amount, computed exactly as the
          monthly fee run would bill it (rounded to the cent, fee first, then VAT). To put
          this example in Annex I, tick an Annex I PDF in the templates above and press
          Download.
        </Text>
        <FeeCalculator value={value} onChange={onChange} />
      </Stack>
    </Card>
  );
}
